import React from 'react';
import { FlaskConical, Building2 } from 'lucide-react';
import {
  PharmacyInfo,
  RoutingItem,
  resolveFulfillmentPharmacy,
} from '../../lib/pharmacyRouting';

const STYLES: Record<string, { wrap: string; icon: React.ComponentType<{ className?: string }> }> = {
  bluefive: { wrap: 'bg-blue-50 text-blue-700 border-blue-200', icon: FlaskConical },
  greenstone: { wrap: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: Building2 },
};

interface PharmacyBadgeProps {
  pharmacy: PharmacyInfo | null | undefined;
  /** Prefix the pharmacy name, e.g. "Filled by Blue Five Labs". */
  withLabel?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}

/** Small pill showing which pharmacy fills an order. Renders nothing if unknown. */
export const PharmacyBadge: React.FC<PharmacyBadgeProps> = ({
  pharmacy,
  withLabel = false,
  size = 'md',
  className = '',
}) => {
  if (!pharmacy) return null;
  const style = STYLES[pharmacy.key] ?? STYLES.greenstone;
  const Icon = style.icon;
  const pad = size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-sm';
  const iconSize = size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5';

  return (
    <span
      title={pharmacy.blurb}
      className={`inline-flex items-center gap-1.5 rounded-full border font-medium ${pad} ${style.wrap} ${className}`}
    >
      <Icon className={iconSize} />
      {withLabel && <span className="opacity-70">Filled by</span>}
      {pharmacy.name}
    </span>
  );
};

interface OrderPharmacyBadgeProps {
  items: RoutingItem[] | null | undefined;
  shipToState?: string | null;
  withLabel?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}

/** Convenience: resolve the fulfilling pharmacy from order items + ship-to state. */
export const OrderPharmacyBadge: React.FC<OrderPharmacyBadgeProps> = ({
  items,
  shipToState,
  ...rest
}) => <PharmacyBadge pharmacy={resolveFulfillmentPharmacy(items, shipToState)} {...rest} />;

export default PharmacyBadge;
